import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui';
import { paymentService } from '@/services/payment';
import { cn } from '@/lib/utils';

interface PaymentReceiptLinkProps {
  paymentId: string;
  hasReceipt: boolean;
  className?: string;
  variant?: 'link' | 'button';
}

export function PaymentReceiptLink({
  paymentId,
  hasReceipt,
  className,
  variant = 'link',
}: PaymentReceiptLinkProps) {
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!hasReceipt) return null;

  const openReceipt = async () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setLoading(true);
    try {
      const res = await paymentService.getReceiptUrl(paymentId);
      if (res.success && res.data?.receiptUrl) {
        setReceiptUrl(res.data.receiptUrl);
        window.open(res.data.receiptUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      toast.error(res.error || 'Receipt not available');
    } catch {
      toast.error('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'button') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn('gap-2', className)}
        onClick={() => void openReceipt()}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <ExternalLink className="h-4 w-4" aria-hidden />
        )}
        View receipt
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void openReceipt()}
      disabled={loading}
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:opacity-60',
        className
      )}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      )}
      View receipt
    </button>
  );
}
